import type {
  EstimateGasArgs,
  GasEstimate,
} from "../../wallet-portfolio/types";

// 외부에서 estimateGas 함수를 주입하지 않는 dApp (예: 직접 WalletInfo/SendPage
// 사용) 을 위해, chain-info API 가 알려준 RPC URL 로 raw JSON-RPC 호출을 보내
// 가스 정보를 추정한다.
//
// 정책: crossy-sdk-js `docs/06-gas-fee.md` §2 / §4 와 동일.
//   1. eth_estimateGas       → gasLimit
//   2. eth_getBlockByNumber  → baseFeePerGas 존재?
//        ├ 존재 → Dynamic: maxPriorityFeePerGas (eth_maxPriorityFeePerGas
//        │         또는 1 Gwei 폴백), maxFeePerGas = baseFee + priority
//        └ 없음 → Legacy:  eth_gasPrice (또는 2 Gwei 폴백)
//   3. BSC(56) / BSC Testnet(97) 은 EIP-1559 미지원 — 강제 Legacy
//
// 모든 wei-scale 값은 BigInt 로 처리한다 (CLAUDE.md `NUMERIC_HANDLING`).

const LEGACY_GAS_PRICE_FALLBACK = 2_000_000_000n; // 2 Gwei
const DYNAMIC_PRIORITY_FALLBACK = 1_000_000_000n; // 1 Gwei
const RPC_TIMEOUT_MS = 8_000;

interface JsonRpcResponse<T> {
  jsonrpc: "2.0";
  id: number | string;
  result?: T;
  error?: { code: number; message: string };
}

async function rpcCall<T>(
  rpcUrl: string,
  method: string,
  params: unknown[],
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), RPC_TIMEOUT_MS);
  const res = await fetch(rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
    signal: controller.signal,
  }).finally(() => clearTimeout(timeout));
  if (!res.ok) {
    throw new Error(`RPC ${method} HTTP ${res.status}`);
  }
  const json = (await res.json()) as JsonRpcResponse<T>;
  if (json.error) {
    throw new Error(`RPC ${method}: ${json.error.message}`);
  }
  if (json.result === undefined) {
    throw new Error(`RPC ${method}: empty result`);
  }
  return json.result;
}

function hexToBigInt(hex: string): bigint {
  return BigInt(hex);
}

function bigIntToHex(value: bigint): `0x${string}` {
  return `0x${value.toString(16)}`;
}

function buildTxParam(args: EstimateGasArgs): Record<string, string> {
  const tx: Record<string, string> = { to: args.to };
  if (args.from) tx.from = args.from;
  if (args.value !== undefined && args.value > 0n) {
    tx.value = bigIntToHex(args.value);
  }
  if (args.data) tx.data = args.data;
  return tx;
}

function isLegacyOnlyChain(chainId: number | undefined): boolean {
  return chainId === 56 || chainId === 97;
}

interface FeeFields {
  gasPrice?: bigint;
  maxFeePerGas?: bigint;
  maxPriorityFeePerGas?: bigint;
}

async function fetchLegacyFees(rpcUrl: string): Promise<FeeFields> {
  try {
    const hex = await rpcCall<string>(rpcUrl, "eth_gasPrice", []);
    return { gasPrice: hexToBigInt(hex) };
  } catch {
    return { gasPrice: LEGACY_GAS_PRICE_FALLBACK };
  }
}

async function fetchDynamicFees(
  rpcUrl: string,
  baseFee: bigint,
): Promise<FeeFields> {
  let priority = DYNAMIC_PRIORITY_FALLBACK;
  try {
    const hex = await rpcCall<string>(rpcUrl, "eth_maxPriorityFeePerGas", []);
    priority = hexToBigInt(hex);
  } catch {
    // node가 EIP-1559 method 미지원 → 1 Gwei 폴백 유지
  }
  return {
    maxFeePerGas: baseFee + priority,
    maxPriorityFeePerGas: priority,
  };
}

async function fetchFees(
  rpcUrl: string,
  legacyOnly: boolean,
): Promise<FeeFields> {
  if (legacyOnly) return fetchLegacyFees(rpcUrl);

  // Dynamic 우선: latest 블록의 baseFeePerGas 유무로 판별.
  let baseFeeHex: string | undefined;
  try {
    const block = await rpcCall<{ baseFeePerGas?: string } | null>(
      rpcUrl,
      "eth_getBlockByNumber",
      ["latest", false],
    );
    baseFeeHex = block?.baseFeePerGas;
  } catch {
    // block 조회 실패 → legacy 폴백으로 떨어진다
  }

  if (baseFeeHex) {
    return fetchDynamicFees(rpcUrl, hexToBigInt(baseFeeHex));
  }
  return fetchLegacyFees(rpcUrl);
}

export async function fallbackEstimateGas(
  rpcUrl: string,
  args: EstimateGasArgs,
): Promise<GasEstimate> {
  const legacyOnly = isLegacyOnlyChain(args.chainId);
  const txParam = buildTxParam(args);

  // gasLimit 추정과 fee 조회는 서로 독립적이므로 병렬 실행
  // (gas-fee 문서 §2 "병렬 RPC 최적화" 와 같은 패턴).
  const [gasLimitHex, fees] = await Promise.all([
    rpcCall<string>(rpcUrl, "eth_estimateGas", [txParam]),
    fetchFees(rpcUrl, legacyOnly),
  ]);

  return {
    gasLimit: hexToBigInt(gasLimitHex),
    gasPrice: fees.gasPrice,
    maxFeePerGas: fees.maxFeePerGas,
    maxPriorityFeePerGas: fees.maxPriorityFeePerGas,
  };
}
